import React, { memo, useMemo } from 'react';
import { Handle, Position, NodeProps, useEdges } from 'reactflow';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, Divider } from '@mui/material';
import { ImportExport as DioIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { getCorFibra } from '../../../utils/fiberColors';
import type { PadraoTuboLoose } from '../../../services/caboService';
import { getPadraoTuboLooseLabel } from '../../../services/caboService';
import { useDiagramInteractive } from '../../../utils/useDiagramInteractive';

interface DIONodeData {
  id: number;
  label: string;
  tipo: string;
  config: string;
  color: string;
  outputs: number;
  qtde_portas?: number;
  padrao_cores?: PadraoTuboLoose;
  onDelete?: (id: number) => void;
  onEditDioPadrao?: (splitterId: number) => void;
  opticalSignal?: {
    inputSignal: number | null;
    outputSignals: Record<string, number>;
  };
}

const DIO_COLOR = '#FF5722';
const PORTAS_POR_GRUPO = 12;

const portLabelSx = {
  fontSize: '0.58rem',
  fontWeight: 700,
  color: '#444',
  backgroundColor: 'rgba(255,255,255,0.92)',
  padding: '0 3px',
  borderRadius: '2px',
  lineHeight: 1.2,
  minWidth: 16,
  textAlign: 'center' as const,
  border: '1px solid #e0e0e0',
  userSelect: 'none' as const,
};

const DIONode: React.FC<NodeProps<DIONodeData>> = ({ data, selected, id }) => {
  const edges = useEdges();
  const isDiagramInteractive = useDiagramInteractive();
  const qtdePortas = data.qtde_portas ?? data.outputs ?? 12;
  const padraoCores = data.padrao_cores ?? 'abnt';

  const grupos = useMemo(() => {
    const total = Math.ceil(qtdePortas / PORTAS_POR_GRUPO);
    return Array.from({ length: total }, (_, g) => {
      const grupoNum = g + 1;
      const inicio = g * PORTAS_POR_GRUPO + 1;
      const fim = Math.min((g + 1) * PORTAS_POR_GRUPO, qtdePortas);
      const portas = Array.from({ length: fim - inicio + 1 }, (_, i) => inicio + i);
      return { grupoNum, inicio, fim, portas };
    });
  }, [qtdePortas]);

  const isHandleConnected = (handleId: string, handleType: 'source' | 'target'): boolean => {
    return edges.some((edge) => {
      if (handleType === 'source') {
        return edge.source === id && edge.sourceHandle === handleId;
      }
      return edge.target === id && edge.targetHandle === handleId;
    });
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onDelete?.(data.id);
  };

  const handleEditPadrao = (e: React.MouseEvent) => {
    e.stopPropagation();
    data.onEditDioPadrao?.(data.id);
  };

  const renderPortaRow = (numeroPorta: number) => {
    const corFibra = getCorFibra(numeroPorta, padraoCores);
    const inputId = `input-fo${numeroPorta}`;
    const outputId = `output-fo${numeroPorta}`;

    const handleStyle = {
      background: corFibra.cor,
      border: corFibra.nome === 'Branco' ? '2px solid #ccc' : '2px solid #fff',
      width: 10,
      height: 10,
      position: 'relative' as const,
      boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      flexShrink: 0,
    };

    const signalOut = data.opticalSignal?.outputSignals?.[outputId];
    const signalLabel = signalOut !== undefined && signalOut !== null
      ? `${signalOut.toFixed(2)} dBm`
      : null;

    return (
      <Box
        key={`dio-porta-${numeroPorta}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 18,
          py: 0.15,
          px: 0.3,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 0.5 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 44 }}>
          <Tooltip title={`Entrada ${numeroPorta} — ${corFibra.nome}`} arrow>
            <Handle
              type="target"
              position={Position.Left}
              id={inputId}
              isConnectable={!isHandleConnected(inputId, 'target')}
              style={handleStyle}
            />
          </Tooltip>
          <Typography sx={portLabelSx}>{numeroPorta}</Typography>
        </Box>

        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: corFibra.cor,
            border: corFibra.nome === 'Branco' ? '1px solid #ccc' : 'none',
            flexShrink: 0,
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 44, justifyContent: 'flex-end', position: 'relative' }}>
          <Typography sx={portLabelSx}>{numeroPorta}</Typography>
          <Tooltip title={`Saída ${numeroPorta} — ${corFibra.nome}`} arrow>
            <Handle
              type="source"
              position={Position.Right}
              id={outputId}
              isConnectable={!isHandleConnected(outputId, 'source')}
              style={handleStyle}
            />
          </Tooltip>
          {signalLabel && (
            <Typography
              sx={{
                position: 'absolute',
                right: -52,
                fontSize: '0.52rem',
                fontWeight: 'bold',
                color: '#4caf50',
                backgroundColor: 'rgba(255,255,255,0.9)',
                padding: '0 2px',
                borderRadius: '2px',
                whiteSpace: 'nowrap',
              }}
            >
              {signalLabel}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Paper
      elevation={selected ? 8 : 3}
      sx={{
        minWidth: 180,
        border: selected ? `2px solid ${DIO_COLOR}` : `1px solid ${DIO_COLOR}`,
        borderRadius: 2,
        backgroundColor: `${DIO_COLOR}12`,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'visible',
        '&:hover .delete-button': isDiagramInteractive ? { opacity: 1 } : {},
        '&:hover .edit-dio-button': isDiagramInteractive ? { opacity: 1 } : {},
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: DIO_COLOR }} />

      {isDiagramInteractive && (
      <>
      <Tooltip title="Editar padrão de cores" arrow>
        <IconButton
          className="edit-dio-button"
          onClick={handleEditPadrao}
          size="small"
          sx={{
            position: 'absolute',
            top: -8,
            left: -8,
            backgroundColor: '#1976d2',
            color: 'white',
            width: 20,
            height: 20,
            opacity: 0,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#1565c0', transform: 'scale(1.1)' },
            zIndex: 20,
          }}
        >
          <EditIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>

      <IconButton
        className="delete-button"
        onClick={handleDelete}
        size="small"
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: '#f44336',
          color: 'white',
          width: 20,
          height: 20,
          opacity: 0,
          transition: 'all 0.2s ease',
          '&:hover': { backgroundColor: '#d32f2f', transform: 'scale(1.1)' },
          zIndex: 20,
        }}
      >
        <DeleteIcon sx={{ fontSize: 12 }} />
      </IconButton>
      </>
      )}

      <Box sx={{ p: 1, pt: 1.2, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.3, mb: 0.3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DioIcon sx={{ fontSize: 18, color: DIO_COLOR }} />
            <Chip
              label="DIO"
              size="small"
              sx={{ backgroundColor: DIO_COLOR, color: 'white', fontSize: '0.7rem', height: 20 }}
            />
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem', color: '#333' }}>
            {data.label || `DIO ${qtdePortas}p`}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>
            {qtdePortas} portas • {grupos.length} grupo{grupos.length > 1 ? 's' : ''} de 12 • {getPadraoTuboLooseLabel(padraoCores)}
          </Typography>
        </Box>

        {grupos.map((grupo, idx) => (
          <Box key={`grupo-${grupo.grupoNum}`}>
            {idx > 0 && <Divider sx={{ mb: 0.6, borderColor: `${DIO_COLOR}55` }} />}
            <Box
              sx={{
                border: `1.5px solid ${DIO_COLOR}66`,
                borderRadius: 1,
                backgroundColor: 'rgba(255,255,255,0.65)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 0.8,
                  py: 0.35,
                  backgroundColor: `${DIO_COLOR}22`,
                  borderBottom: `1px solid ${DIO_COLOR}44`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: DIO_COLOR }}>
                  Grupo {grupo.grupoNum}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: '#666' }}>
                  Portas {grupo.inicio}–{grupo.fim}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  px: 0.8,
                  py: 0.25,
                  borderBottom: '1px solid #eee',
                  backgroundColor: '#fafafa',
                }}
              >
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#888' }}>Entrada</Typography>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 600, color: '#888' }}>Saída</Typography>
              </Box>

              <Box sx={{ px: 0.4, py: 0.3 }}>
                {grupo.portas.map(renderPortaRow)}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default memo(DIONode);
